import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  HostListener,
  OnDestroy,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { filter, map, mergeMap } from 'rxjs';
import { HeaderComponent } from './header/header.component';
import { FooterComponent } from './footer/footer.component';
import * as THREE from 'three';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, FooterComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements AfterViewInit, OnDestroy {
  title = 'angular-starter-template';

  @ViewChild('rendererContainer', { static: false })
  rendererContainer!: ElementRef;

  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private gridMesh!: THREE.Mesh;
  private sunMesh!: THREE.Mesh;
  private animationFrameId: number | null = null;
  private clock = new THREE.Clock();

  private mouseX = 0;
  private mouseY = 0;

  private platformId: object = inject(PLATFORM_ID) as object;
  private router = inject(Router);
  private titleService = inject(Title);
  private metaService = inject(Meta);

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.router.routerState.root),
        map((route) => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        mergeMap((route) => route.data)
      )
      .subscribe((data) => {
        const title = data['title'];
        const description = data['description'];
        if (title) this.titleService.setTitle(title);
        if (description) {
          this.metaService.updateTag({ name: 'description', content: description });
          this.metaService.updateTag({ property: 'og:description', content: description });
          this.metaService.updateTag({ name: 'twitter:description', content: description });
        }
        if (title) {
          this.metaService.updateTag({ property: 'og:title', content: title });
          this.metaService.updateTag({ name: 'twitter:title', content: title });
        }
      });
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.initThreeJs();
    }
  }

  ngOnDestroy(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.renderer) {
      this.renderer.dispose();
    }
  }

  private initThreeJs(): void {
    // 1. Scene
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x09090b, 0.012);

    // 2. Camera
    this.camera = new THREE.PerspectiveCamera(
      85,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.set(0, 10, 100);

    // 3. Renderer
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.toneMapping = THREE.ReinhardToneMapping;
    this.rendererContainer.nativeElement.appendChild(this.renderer.domElement);

    // 4. Subtle Grid
    const gridSize = 400;
    const segments = 80;
    const gridGeometry = new THREE.PlaneGeometry(
      gridSize,
      gridSize,
      segments,
      segments
    );

    gridGeometry.rotateX(-Math.PI / 2);

    const positions = gridGeometry.attributes['position'].array as Float32Array;
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const distFromCenter = Math.abs(x);
      if (distFromCenter > 20) {
        positions[i + 1] = Math.random() * (distFromCenter * 0.3);
      }
    }

    const gridMaterial = new THREE.MeshBasicMaterial({
      color: 0x8b5cf6, // Violet accent
      wireframe: true,
      transparent: true,
      opacity: 0.08,
    });

    this.gridMesh = new THREE.Mesh(gridGeometry, gridMaterial);
    this.gridMesh.position.y = -15;
    this.scene.add(this.gridMesh);

    // 5. Subtle gradient orb (replaces synthwave sun)
    const sunGeometry = new THREE.CircleGeometry(40, 64);

    const sunMaterial = new THREE.ShaderMaterial({
      uniforms: {
        color1: { value: new THREE.Color(0x8b5cf6) }, // Violet
        color2: { value: new THREE.Color(0x06b6d4) }, // Cyan
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 color1;
        uniform vec3 color2;
        varying vec2 vUv;
        void main() {
          vec3 mixed = mix(color2, color1, vUv.y);
          float alpha = smoothstep(0.0, 0.5, vUv.y) * 0.25;
          gl_FragColor = vec4(mixed, alpha);
        }
      `,
      transparent: true,
      side: THREE.DoubleSide,
    });

    this.sunMesh = new THREE.Mesh(sunGeometry, sunMaterial);
    this.sunMesh.position.set(0, 20, -100);
    this.scene.add(this.sunMesh);

    // 6. Subtle Particles
    const particleGeo = new THREE.BufferGeometry();
    const particleCount = 500;
    const particlePos = new Float32Array(particleCount * 3);
    const particleColor = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount * 3; i += 3) {
      particlePos[i] = (Math.random() - 0.5) * 200;
      particlePos[i + 1] = Math.random() * 50 - 15;
      particlePos[i + 2] = (Math.random() - 0.5) * 200;

      // Muted violet / gray particles
      const hue = 0.72 + Math.random() * 0.08; // violet range
      const c = new THREE.Color().setHSL(hue, 0.3, 0.45);
      particleColor[i] = c.r;
      particleColor[i + 1] = c.g;
      particleColor[i + 2] = c.b;
    }

    particleGeo.setAttribute(
      'position',
      new THREE.BufferAttribute(particlePos, 3)
    );
    particleGeo.setAttribute(
      'color',
      new THREE.BufferAttribute(particleColor, 3)
    );

    const particleMat = new THREE.PointsMaterial({
      size: 1.2,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      transparent: true,
      opacity: 0.6,
    });

    const particlesMesh = new THREE.Points(particleGeo, particleMat);
    this.scene.add(particlesMesh);

    // 7. Animation Loop
    const animate = () => {
      this.animationFrameId = requestAnimationFrame(animate);
      const time = this.clock.getElapsedTime();

      this.gridMesh.position.z = (time * 4) % 20;

      this.sunMesh.position.y = 20 + Math.sin(time * 0.5) * 1;

      const positions = particlesMesh.geometry.attributes['position']
        .array as Float32Array;
      for (let i = 2; i < positions.length; i += 3) {
        positions[i] += 0.08;
        if (positions[i] > 100) positions[i] = -100;
      }
      particlesMesh.geometry.attributes['position'].needsUpdate = true;

      this.camera.position.x +=
        (this.mouseX * 0.05 - this.camera.position.x) * 0.03;
      this.camera.position.y +=
        (-this.mouseY * 0.03 + 10 - this.camera.position.y) * 0.03;
      this.camera.lookAt(
        this.sunMesh.position.x,
        this.sunMesh.position.y,
        this.sunMesh.position.z
      );

      this.renderer.render(this.scene, this.camera);
    };

    animate();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.camera && this.renderer) {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    }
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    this.mouseX = (event.clientX / window.innerWidth) * 2 - 1;
    this.mouseY = -(event.clientY / window.innerHeight) * 2 + 1;
    this.mouseX *= 50;
    this.mouseY *= 50;
  }
}
